import React from "react";
import { AeroSelectedFlight } from "../../types/darwin";
import {
    formatDate,
    formatTime,
    formatDuration,
    AERO_AIRPORTS,
} from "../../services/darwinApi";

interface AeroFlightSummaryCardProps {
    label: "Departure" | "Return";
    selectedFlight: AeroSelectedFlight;
}

const FARE_POLICY =
    "Fully refundable to credit up to 21 days before departure, or refundable to cash with a 3% transactional fee. Cancellations within 21 days incur a 15% fee (refunded as credit), and within 72 hours a 50% fee. Nonrefundable within 12 hours of departure.";

const AeroFlightSummaryCard: React.FC<AeroFlightSummaryCardProps> = ({
    label,
    selectedFlight,
}) => {
    const { trip, ptcFare } = selectedFlight;
    const flight = trip.tripFlight;

    const originAirport = AERO_AIRPORTS[flight.origin];
    const destAirport = AERO_AIRPORTS[flight.destination];

    // Format as "1H5MIN" matching aero.com style
    const aeroDuration = formatDuration(flight.duration)
        .replace(/\s+/g, "")
        .replace(/M$/, "MIN");

    return (
        <div
            style={{
                backgroundColor: "#fff",
                borderRadius: 16,
                boxShadow: "rgba(0,0,0,0.06) 0px 4px 15px",
                maxWidth: 480,
                padding: 24,
            }}
        >
            {/* Label — "Departure" / "Return" */}
            <div
                style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: "#949494",
                    lineHeight: "16px",
                    marginBottom: 8,
                }}
            >
                {label}
            </div>

            {/* Date */}
            <div
                style={{
                    fontSize: 28,
                    fontWeight: 400,
                    color: "#000",
                    lineHeight: "32px",
                }}
            >
                {formatDate(flight.departureDate)}
            </div>

            {/* Divider */}
            <div style={{ height: 1, backgroundColor: "#f3f3f3", margin: "16px 0" }} />

            {/* Origin: IATA + Time row */}
            <div
                style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    marginBottom: 8,
                }}
            >
                <span
                    style={{
                        fontSize: 28,
                        fontWeight: 400,
                        color: "#000",
                        lineHeight: "32px",
                    }}
                >
                    {flight.origin}
                </span>
                <span
                    style={{
                        fontSize: 28,
                        fontWeight: 400,
                        color: "#000",
                        lineHeight: "32px",
                    }}
                >
                    {formatTime(flight.departureTime)}
                </span>
            </div>

            {/* Origin city / airport / terminal */}
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span
                    style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#000",
                        lineHeight: "16px",
                    }}
                >
                    {originAirport?.city ?? flight.origin}
                </span>
                <span
                    style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#757575",
                        lineHeight: "16px",
                    }}
                >
                    {originAirport?.name ?? ""}
                </span>
                {originAirport?.terminal && (
                    <span
                        style={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: "#949494",
                            lineHeight: "16px",
                        }}
                    >
                        {originAirport.terminal}
                    </span>
                )}
            </div>

            {/* Duration row — gray line / text / gray line */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    margin: "16px 0",
                }}
            >
                <div style={{ flex: "0 1 auto", height: 1, width: "40%", backgroundColor: "#f3f3f3" }} />
                <div
                    style={{
                        flex: "1 1 0%",
                        textAlign: "center",
                        margin: "0 16px",
                    }}
                >
                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#c6c6c6",
                            lineHeight: "12px",
                        }}
                    >
                        {aeroDuration}
                    </span>
                </div>
                <div style={{ flex: "0 1 auto", height: 1, width: "40%", backgroundColor: "#f3f3f3" }} />
            </div>

            {/* Destination: IATA + Time row */}
            <div
                style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    marginBottom: 8,
                }}
            >
                <span
                    style={{
                        fontSize: 28,
                        fontWeight: 400,
                        color: "#000",
                        lineHeight: "32px",
                    }}
                >
                    {flight.destination}
                </span>
                <span
                    style={{
                        fontSize: 28,
                        fontWeight: 400,
                        color: "#000",
                        lineHeight: "32px",
                    }}
                >
                    {formatTime(flight.arrivalTime)}
                    {flight.arrivalDateAdjustor > 0 && (
                        <sup
                            style={{
                                fontSize: 12,
                                color: "#fb6900",
                                marginLeft: 2,
                            }}
                        >
                            +{flight.arrivalDateAdjustor}
                        </sup>
                    )}
                </span>
            </div>

            {/* Destination city / airport / terminal */}
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span
                    style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#000",
                        lineHeight: "16px",
                    }}
                >
                    {destAirport?.city ?? flight.destination}
                </span>
                <span
                    style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#757575",
                        lineHeight: "16px",
                    }}
                >
                    {destAirport?.name ?? ""}
                </span>
                {destAirport?.terminal && (
                    <span
                        style={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: "#949494",
                            lineHeight: "16px",
                        }}
                    >
                        {destAirport.terminal}
                    </span>
                )}
            </div>

            {/* Divider */}
            <div style={{ height: 1, backgroundColor: "#f3f3f3", margin: "24px 0" }} />

            {/* Per person price */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                }}
            >
                <span
                    style={{
                        fontSize: 24,
                        fontWeight: 400,
                        color: "#000",
                        lineHeight: "28px",
                    }}
                >
                    Per person
                </span>
                <span
                    style={{
                        fontSize: 24,
                        fontWeight: 400,
                        color: "#000",
                        lineHeight: "28px",
                    }}
                >
                    $
                    {ptcFare.totalFare.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}
                </span>
            </div>

            {/* Divider */}
            <div style={{ height: 1, backgroundColor: "#f3f3f3", margin: "24px 0" }} />

            {/* Aero Select fare section — orange left border */}
            <div style={{ borderLeft: "2px solid #fb6900", padding: "8px 0 8px 16px" }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 8,
                    }}
                >
                    <span
                        style={{
                            fontSize: 24,
                            fontWeight: 400,
                            color: "#000",
                            lineHeight: "28px",
                        }}
                    >
                        Aero Select
                    </span>
                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#fff",
                            backgroundColor: "#fb6900",
                            padding: "3px 8px",
                            borderRadius: 4,
                            lineHeight: "16px",
                        }}
                    >
                        Updated terms
                    </span>
                </div>
                <div
                    style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: "#949494",
                        lineHeight: "16px",
                    }}
                >
                    {FARE_POLICY}
                </div>
            </div>

            {/* Divider */}
            <div style={{ height: 1, backgroundColor: "#f3f3f3", margin: "24px 0" }} />

            {/* Footer */}
            <div
                style={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: "#949494",
                    lineHeight: "16px",
                }}
            >
                Flight operated by USAC Airways 695 LLC dba Aero
            </div>
        </div>
    );
};

export default AeroFlightSummaryCard;
